import { invoke } from '@tauri-apps/api/core';
import { DB_QUERY_COMMANDS, quoteIdentFor } from '../extensions/dbEngines';

// ── DB schema introspection (SCHEMA ONLY — never selects row data) ────────────

export async function runRawQuery(engine, extensionId, connId, sql, params = []) {
  const cmd = DB_QUERY_COMMANDS[engine];
  if (!cmd) throw new Error(`Unsupported engine: ${engine}`);
  return invoke(cmd, { extensionId, connId, sql, params });
}

export async function fetchDbSchema(engine, extensionId, connId, dbName) {
  if (engine === 'sqlite') return fetchSqliteSchema(extensionId, connId);
  if (engine === 'postgres') return fetchPgSchema(extensionId, connId, dbName);
  return fetchMysqlSchema(extensionId, connId, dbName);
}

export function groupTables(tableRows, colByTable, viewNames) {
  const tables = [];
  const views = [];
  for (const [name, type] of tableRows) {
    const entry = { name, columns: colByTable.get(name) || [] };
    if (viewNames.has(name) || /VIEW/i.test(type || '')) views.push(entry);
    else tables.push(entry);
  }
  return { tables, views };
}

export async function fetchMysqlSchema(extensionId, connId, dbName) {
  const tableSql = dbName
    ? 'SELECT table_name, table_type FROM information_schema.tables WHERE table_schema = ? ORDER BY table_name'
    : 'SELECT table_name, table_type FROM information_schema.tables WHERE table_schema = DATABASE() ORDER BY table_name';
  const colSql = dbName
    ? 'SELECT table_name, column_name, data_type, is_nullable, column_key FROM information_schema.columns WHERE table_schema = ? ORDER BY table_name, ordinal_position'
    : 'SELECT table_name, column_name, data_type, is_nullable, column_key FROM information_schema.columns WHERE table_schema = DATABASE() ORDER BY table_name, ordinal_position';
  const params = dbName ? [dbName] : [];
  const [tRes, cRes] = await Promise.all([
    runRawQuery('mysql', extensionId, connId, tableSql, params),
    runRawQuery('mysql', extensionId, connId, colSql, params),
  ]);
  const colByTable = new Map();
  for (const r of cRes.rows || []) {
    const [tbl, col, type, nullable, key] = r;
    if (!colByTable.has(tbl)) colByTable.set(tbl, []);
    colByTable.get(tbl).push({ name: col, type, nullable: nullable === 'YES', pk: key === 'PRI' });
  }
  return groupTables((tRes.rows || []).map((r) => [r[0], r[1]]), colByTable, new Set());
}

export async function fetchPgSchema(extensionId, connId, dbName) {
  const schemaExpr = dbName ? '$1' : 'current_schema()';
  const params = dbName ? [dbName] : [];
  const tableSql = `SELECT table_name, table_type FROM information_schema.tables WHERE table_schema = ${schemaExpr} ORDER BY table_name`;
  const colSql = `SELECT table_name, column_name, data_type, is_nullable FROM information_schema.columns WHERE table_schema = ${schemaExpr} ORDER BY table_name, ordinal_position`;
  const pkSql = `SELECT kcu.table_name, kcu.column_name FROM information_schema.table_constraints tc JOIN information_schema.key_column_usage kcu ON tc.constraint_name = kcu.constraint_name AND tc.table_schema = kcu.table_schema WHERE tc.constraint_type = 'PRIMARY KEY' AND tc.table_schema = ${schemaExpr}`;
  const [tRes, cRes, pkRes] = await Promise.all([
    runRawQuery('postgres', extensionId, connId, tableSql, params),
    runRawQuery('postgres', extensionId, connId, colSql, params),
    runRawQuery('postgres', extensionId, connId, pkSql, params).catch(() => ({ rows: [] })),
  ]);
  const pks = new Set((pkRes.rows || []).map((r) => `${r[0]}\u0000${r[1]}`));
  const colByTable = new Map();
  for (const r of cRes.rows || []) {
    const [tbl, col, type, nullable] = r;
    if (!colByTable.has(tbl)) colByTable.set(tbl, []);
    colByTable.get(tbl).push({ name: col, type, nullable: nullable === 'YES', pk: pks.has(`${tbl}\u0000${col}`) });
  }
  return groupTables((tRes.rows || []).map((r) => [r[0], r[1]]), colByTable, new Set());
}

export async function fetchSqliteSchema(extensionId, connId) {
  const tRes = await runRawQuery(
    'sqlite', extensionId, connId,
    "SELECT name, type FROM sqlite_master WHERE type IN ('table','view') AND name NOT LIKE 'sqlite_%' ORDER BY name",
    []
  );
  const rows = tRes.rows || [];
  const colByTable = new Map();
  const viewNames = new Set();
  for (const r of rows) {
    const name = r[0];
    if ((r[1] || '').toLowerCase() === 'view') viewNames.add(name);
    // PRAGMA rows: [cid, name, type, notnull, dflt_value, pk]
    const info = await runRawQuery('sqlite', extensionId, connId, `PRAGMA table_info(${quoteIdentFor('sqlite', name)})`, []);
    colByTable.set(name, (info.rows || []).map((c) => ({
      name: String(c[1]),
      type: c[2] || '',
      nullable: Number(c[3]) === 0,
      pk: Number(c[5]) > 0,
    })));
  }
  return groupTables(rows.map((r) => [r[0], r[1]]), colByTable, viewNames);
}
